// src/api/appPacks.js
//
// Runtime App Pack store — the frontend's read layer over the proxy's
// App Pack registry (`GET /proxy/apps` → { apps: { [slug]: pack } }).
//
// Fallback order (never blanks the UI, even if the proxy is unreachable):
//   1. Live fetch from the proxy REST API   (freshest — reflects dashboard edits)
//   2. localStorage cache (`adlensfx_app_packs`)  (last known-good, survives reloads)
//   3. Committed seed JSON under config/packs/*.json (ships with the app, always available)
//
// Sync accessors (getPacks/getPack/findPackByIu) return whatever is cached
// in-memory RIGHT NOW — seed data on the very first render, then live data
// once initAppPacks() resolves. Components render before the fetch lands,
// so every consumer must tolerate the seed-then-live upgrade; there is no
// "loading" state to thread through.
//
// This module follows the same fetch/localStorage/mirror pattern as
// utils/profileSync.js (pullProfiles) — no new state library introduced.

// No branded packs ship — the tool is app-agnostic. Apps come from the remote
// App Pack store / localStorage cache, or the user's own custom apps; out of
// the box the only app is the code-synthesized `generic` (see appProfiles.js).
const SEED_PACKS = {};

const LS_KEY = 'adlensfx_app_packs';

// Resolve the proxy REST API base the same way IuRewritePage.jsx does.
const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

/* ─── module-singleton cache ──────────────────────────────────────────────── */
// Seeded synchronously at import time so getPacks()/getPack()/findPackByIu()
// always have something to return, even before initAppPacks() is ever called.
let _packs = _readLocalStorageCache() || SEED_PACKS;
let _initPromise = null;
const _subscribers = new Set();

function _readLocalStorageCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && Object.keys(parsed).length) return parsed;
    return null;
  } catch {
    return null;
  }
}

function _writeLocalStorageCache(packs) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(packs)); } catch { /* ignore quota/private-mode errors */ }
}

function _notifySubscribers() {
  for (const fn of _subscribers) {
    try { fn(_packs); } catch { /* a subscriber's own error shouldn't break the others */ }
  }
}

/**
 * Subscribe to pack-store updates (fired once, after initAppPacks() resolves
 * with fresh data). Returns an unsubscribe function. Optional — most callers
 * are fine reading synchronously via getPacks()/getPack() after awaiting
 * initAppPacks() once at bootstrap.
 */
export function subscribeAppPacks(fn) {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}

/**
 * Fetch the live pack registry from the proxy once at startup. Safe to call
 * multiple times — idempotent (returns the same in-flight/resolved promise).
 * Always resolves (never rejects) — on any failure the store keeps whatever
 * it already had (localStorage cache or seed JSON).
 */
export function initAppPacks() {
  if (_initPromise) return _initPromise;
  _initPromise = _fetchPacks();
  return _initPromise;
}

/**
 * Re-fetch after a WRITE (the pack editor's POST /proxy/apps/:slug). Callers
 * cannot use initAppPacks() for this: it memoizes on `_initPromise` and returns
 * the already-resolved promise, so a save would leave `_packs`, the
 * localStorage cache and every subscriber holding the pre-save pack until a
 * full page reload — the editor showing one thing and getPack() another. This
 * discards the memo and re-runs the same fetch. Same never-rejects contract.
 */
export function refreshAppPacks() {
  _initPromise = _fetchPacks();
  return _initPromise;
}

function _fetchPacks() {
  return (async () => {
    try {
      const res = await fetch(`${PROXY_API_BASE}/proxy/apps`);
      if (!res.ok) return _packs;
      const data = await res.json();
      if (data && data.apps && typeof data.apps === 'object' && Object.keys(data.apps).length) {
        _packs = data.apps;
        _writeLocalStorageCache(_packs);
        _notifySubscribers();
      }
    } catch {
      // Offline / proxy down — keep localStorage cache or seed fallback, silently.
    }
    return _packs;
  })();
}

/** Full pack map, keyed by slug — synchronous, always populated. */
export function getPacks() {
  return _packs;
}

/** A single pack by slug, or null. */
export function getPack(slug) {
  if (!slug) return null;
  return _packs[slug] || null;
}

/**
 * Parse a GAM IU string (/<networkId>/<brandCode>/...) and return the first
 * enabled pack whose identity.networkId + identity.brandCodes matches.
 * Mirrors appPacks.findByIu() on the proxy (proxy-server/appPacks.js).
 * Returns null if nothing matches (generic mode — same contract as the proxy).
 */
export function findPackByIu(iu = '') {
  const parts = String(iu || '').split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const [networkId, brandCode] = parts;
  for (const pack of Object.values(_packs)) {
    if (!pack || pack.enabled === false) continue;
    const identity = pack.identity || {};
    if (identity.networkId !== networkId) continue;
    const brandCodes = Array.isArray(identity.brandCodes) ? identity.brandCodes : [];
    if (brandCodes.includes(brandCode)) return pack;
  }
  return null;
}

/**
 * Memoized cross-pack union sets, recomputed whenever the pack store updates.
 * Mirrors appPacks.aggregate() on the proxy.
 */
let _aggregateCache = null;
let _aggregateForPacks = null;
export function aggregatePacks() {
  if (_aggregateCache && _aggregateForPacks === _packs) return _aggregateCache;
  const contentHosts = new Set();
  const videoHosts = new Set();
  const ignoredPageHosts = new Set();
  const brandCodes = new Set();
  const networkIds = new Set();
  for (const pack of Object.values(_packs)) {
    if (!pack || pack.enabled === false) continue;
    const ca = pack.contentApis || {};
    (ca.contentHosts || []).forEach(h => contentHosts.add(h));
    (ca.videoHosts || []).forEach(h => videoHosts.add(h));
    (ca.ignoredPageHosts || []).forEach(h => ignoredPageHosts.add(h));
    const identity = pack.identity || {};
    (identity.brandCodes || []).forEach(b => brandCodes.add(b));
    if (identity.networkId) networkIds.add(identity.networkId);
  }
  _aggregateCache = {
    contentHosts: [...contentHosts],
    videoHosts: [...videoHosts],
    ignoredPageHosts: [...ignoredPageHosts],
    brandCodes: [...brandCodes],
    networkIds: [...networkIds],
  };
  _aggregateForPacks = _packs;
  return _aggregateCache;
}

/**
 * GET /proxy/apps/<slug>/meta-placements -> { metaPlacementReport: { [platform]: entry } } ({} when
 * none). Rejects with an Error carrying .status on non-2xx. Global fetch (api/proxyAuth wraps it).
 */
export async function getMetaPlacements(slug) {
  const res = await fetch(`${PROXY_API_BASE}/proxy/apps/${encodeURIComponent(slug)}/meta-placements`, {
    headers: { Accept: 'application/json' },
  });
  let json = null;
  try { json = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `meta-placements failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return (json && json.metaPlacementReport) || {};
}

/**
 * POST the Monetization Manager CSV, raw text, Content-Type text/csv, to
 * /proxy/apps/<slug>/meta-placements/import. Resolves the 200 body ({ ok, platforms, entry,
 * rejectedRows }); on 4xx rejects with an Error carrying .status, .unmatched (Property names the
 * pack did not match) and .missing (absent columns).
 */
export async function importMetaPlacements(slug, csvText) {
  const res = await fetch(`${PROXY_API_BASE}/proxy/apps/${encodeURIComponent(slug)}/meta-placements/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'text/csv', Accept: 'application/json' },
    body: String(csvText == null ? '' : csvText),
  });
  let json = null;
  try { json = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `import failed (${res.status})`);
    err.status = res.status;
    err.unmatched = json && Array.isArray(json.unmatched) ? json.unmatched : [];
    err.missing = json && Array.isArray(json.missing) ? json.missing : [];
    throw err;
  }
  return json;
}

/**
 * POST /proxy/app-packs/<slug>/derive-screens — { rows, build?, platform?, accept? }.
 * Without `accept` the proxy answers a PROPOSAL { proposal:{screens}, current } and writes
 * nothing; with `accept:true` it persists and answers { accepted:true, screens }.
 * Uses the global fetch (wrapped by api/proxyAuth — never set Authorization here).
 * Rejects with an Error carrying .status on non-2xx.
 */
export async function deriveScreens(slug, { rows, build, platform, accept } = {}) {
  const res = await fetch(`${PROXY_API_BASE}/proxy/app-packs/${encodeURIComponent(slug)}/derive-screens`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ rows: Array.isArray(rows) ? rows : [], ...(build ? { build } : {}), ...(platform ? { platform } : {}), ...(accept ? { accept: true } : {}) }),
  });
  let json = null;
  try { json = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `derive-screens failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json;
}
